import {
  ActiveNumberItem,
  CatalogMarkup,
  CatalogServiceItem,
  Country,
  CustomerCurrency,
  DashboardData,
  NumberStatus,
  NumberAvailabilityKind,
  NumberType,
  OperationItem,
  PartnerSnapshot,
  LongRentalCatalog,
  LongRentalNumber,
  LongRentalQuote,
  LongRentalPurchaseOrder,
  LongRentalRenewalPreview,
  LongRentalScope,
  PublicLongRentalInbox,
  PublicLongRentalInvoice,
  SharedLongRentalNumber,
  Role,
  SystemInfo,
  TopCountryEntry,
  UserProfile,
  VoiceVerificationCatalog,
  VoiceVerificationNumber,
  VoiceVerificationQuote,
  VoiceVerificationPurchaseOrder,
} from '../types/models';

type JsonRecord = Record<string, unknown>;

interface PurchaseQuotePayload {
  serviceCode: string;
  countryId: number;
  operator?: string | null;
  quantity: number;
  numberType?: NumberType;
  priceAfter?: number;
}

export interface CatalogAvailabilitySummary {
  physicalOnlyServices: number;
  virtualOnlyServices: number;
  mixedServices: number;
  unavailableServices: number;
}

export interface RentAvailability {
  rentAvailable: boolean;
  reasonCode: string;
  reason: string;
  checkedAt: string;
  durationUnit: 'hours' | null;
  supportedDurations: number[];
  exactPriceConfirmed: boolean;
  lifecycleConfirmed: boolean;
}

export interface RentDurationOption {
  hours: number;
  quantity: number;
  providerPrice: number;
  currency: CustomerCurrency;
}

export interface RentCatalogService {
  code: string;
  name: string;
  options: RentDurationOption[];
}

export interface PurchaseQuoteResult {
  serviceCode: string;
  countryCode: number;
  operator?: string | null;
  quantity: number;
  numberType: NumberType;
  retailUnitPrice: number;
  nextFinalUnitPrice?: number | null;
  /** Цена за номер, до которой выдача только что не удалась; стартовый уровень выше неё. */
  recentlyUnavailableFinalUnitPrice?: number | null;
  markupPercent: number;
  retailSubtotal: number;
  markupAmount: number;
  finalTotal: number;
  finalUnitPrice: number;
  currency: CustomerCurrency;
}

export interface PurchaseBuyResult {
  currency: CustomerCurrency;
  replayed: boolean;
  terminal: boolean;
  successful: Array<{
    heroActivationId: string;
    phone: string;
    charged: number;
    currency: CustomerCurrency;
    rentTime?: number;
  }>;
  failed: Array<{
    itemIndex: number;
    state: string;
    step: string;
    code?: string | null;
    reason: string;
  }>;
  pending: Array<{
    itemIndex: number;
    state: string;
  }>;
  totals: {
    requestedQty: number;
    successQty: number;
    failedQty: number;
    pendingQty: number;
    chargedTotal: number;
    currency: CustomerCurrency;
  };
  order: {
    id: string;
    operationKey: string;
    state: 'processing' | 'completed' | 'partial' | 'failed' | 'manual_review';
    finalUnitPrice: number;
    finalTotal: number;
    currency: CustomerCurrency;
    createdAt: string;
    updatedAt: string;
  };
}

export function createClientOperationKey(type: 'purchase' | 'rent' | 'long-rent' | 'long-renew' | 'long-refund' | 'long-cancel' | 'voice' | 'voice-cancel'): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return `sms:${type}:v1:${cryptoApi.randomUUID()}`;
  if (!cryptoApi?.getRandomValues) {
    throw new ApiError('Браузер не поддерживает безопасное создание ключа операции', 500);
  }
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `sms:${type}:v1:${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export class ApiError extends Error {
  status: number;

  details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details ?? null;
  }
}

function isRecord(value: unknown): value is JsonRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toStringValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') {
    return value.trim() || fallback;
  }
  return fallback;
}

function isUsdCurrency(value: unknown): value is CustomerCurrency {
  return toStringValue(value).toUpperCase() === 'USD';
}

function requireUsdCurrency(value: unknown, context: string): CustomerCurrency {
  if (!isUsdCurrency(value)) {
    throw new ApiError(`${context}: сервер не подтвердил валюту USD`, 502);
  }
  return 'USD';
}

function mapRole(rawRole: unknown): Role {
  if (rawRole === 'admin' || rawRole === 'partner' || rawRole === 'client') {
    return rawRole;
  }
  if (rawRole === 'user') {
    return 'client';
  }
  return 'client';
}

function getApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return '/sms';
  }

  const { hostname, port, origin } = window.location;
  const isLocalDev = (hostname === 'localhost' || hostname === '127.0.0.1')
    && (port === '3000' || port === '3001');

  if (isLocalDev) {
    return 'http://localhost:5004';
  }

  return `${origin}/sms`;
}

function getStoredToken(): string | null {
  try {
    const token = localStorage.getItem('token');
    return token && token.trim() ? token : null;
  } catch {
    return null;
  }
}

let refreshPromise: Promise<boolean> | null = null;

function getAuthApiUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  const { hostname, port, origin } = window.location;
  if ((hostname === 'localhost' || hostname === '127.0.0.1') && (port === '3000' || port === '3001')) {
    return `http://localhost:5001${path}`;
  }
  return `${origin}${path}`;
}

async function refreshPortalSession(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    let storedRefreshToken = '';
    try { storedRefreshToken = (localStorage.getItem('refreshToken') || '').trim(); } catch { /* no-op */ }
    try {
      const response = await fetch(getAuthApiUrl('/api/auth/refresh'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
      });
      if (!response.ok) return false;
      const payload = await response.json();
      const accessToken = payload?.data?.tokens?.accessToken
        ?? payload?.data?.accessToken
        ?? payload?.tokens?.accessToken
        ?? payload?.accessToken
        ?? payload?.token;
      const nextRefreshToken = payload?.data?.tokens?.refreshToken
        ?? payload?.tokens?.refreshToken
        ?? payload?.refreshToken;
      if (typeof accessToken !== 'string' || !accessToken.trim()) return false;
      try {
        localStorage.setItem('token', accessToken.trim());
        if (typeof nextRefreshToken === 'string' && nextRefreshToken.trim()) {
          localStorage.setItem('refreshToken', nextRefreshToken.trim());
        }
      } catch { /* общая cookie остаётся рабочим источником сессии */ }
      return true;
    } catch {
      return false;
    }
  })().finally(() => { refreshPromise = null; });

  return refreshPromise;
}

function getErrorMessage(payload: unknown, status: number): string {
  if (isRecord(payload)) {
    const directError = toStringValue(payload.error);
    if (directError) {
      return directError;
    }
  }
  return `Ошибка API (${status})`;
}

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Неизвестная ошибка';
}

async function requestJson<T>(path: string, init: RequestInit = {}, allowRefresh = true): Promise<T> {
  const url = `${getApiBaseUrl()}${path}`;
  const headers = new Headers(init.headers || {});
  headers.set('Accept', 'application/json');

  const token = getStoredToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const hasJsonBody = init.body !== undefined && !(init.body instanceof FormData);
  if (hasJsonBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers,
      credentials: 'include',
    });
  } catch {
    throw new ApiError('Сервер SMS недоступен', 503);
  }

  if (response.status === 401 && allowRefresh && await refreshPortalSession()) {
    return requestJson<T>(path, init, false);
  }

  const rawText = await response.text();
  let payload: unknown = null;
  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      payload = rawText;
    }
  }

  if (!response.ok) {
    throw new ApiError(getErrorMessage(payload, response.status), response.status, payload);
  }

  if (isRecord(payload) && payload.success === false) {
    throw new ApiError(getErrorMessage(payload, response.status), response.status, payload);
  }

  if (isRecord(payload) && Object.prototype.hasOwnProperty.call(payload, 'data')) {
    return payload.data as T;
  }

  return payload as T;
}

function normalizeCatalogMarkup(raw: unknown): CatalogMarkup {
  const source = isRecord(raw) ? raw : {};
  return {
    markupPercent: toNumber(source.markupPercent, 0),
  };
}

function normalizeHeroCountry(raw: unknown): Country | null {
  if (!isRecord(raw)) return null;

  const id = toNumber(raw.id, NaN);
  if (!Number.isFinite(id)) return null;

  return {
    id,
    titleRu: toStringValue(raw.titleRu, toStringValue(raw.titleEn, String(id))),
    titleEn: toStringValue(raw.titleEn, toStringValue(raw.titleRu, String(id))),
    titleCn: toStringValue(raw.titleCn),
    visible: Number(raw.visible) === 1,
    retry: Number(raw.retry) === 1,
    rent: Number(raw.rent) === 1,
    multiService: Number(raw.multiService) === 1,
  };
}

function normalizeCatalogService(raw: unknown): CatalogServiceItem | null {
  if (!isRecord(raw)) return null;

  const code = toStringValue(raw.code);
  if (!code) return null;

  const country = normalizeHeroCountry(raw.country);
  const hasPrice = Object.prototype.hasOwnProperty.call(raw, 'retailPrice')
    || Object.prototype.hasOwnProperty.call(raw, 'finalPrice');
  if (hasPrice && !isUsdCurrency(raw.currency)) return null;

  return {
    code,
    name: toStringValue(raw.name || raw.title, code),
    country: country || undefined,
    retailPrice: Object.prototype.hasOwnProperty.call(raw, 'retailPrice') ? toNumber(raw.retailPrice, 0) : undefined,
    finalPrice: Object.prototype.hasOwnProperty.call(raw, 'finalPrice') ? toNumber(raw.finalPrice, 0) : undefined,
    markupPercent: Object.prototype.hasOwnProperty.call(raw, 'markupPercent') ? toNumber(raw.markupPercent, 0) : undefined,
    count: Object.prototype.hasOwnProperty.call(raw, 'count') ? Math.max(0, Math.floor(toNumber(raw.count, 0))) : undefined,
    totalCount: Object.prototype.hasOwnProperty.call(raw, 'totalCount') ? Math.max(0, Math.floor(toNumber(raw.totalCount, 0))) : undefined,
    physicalCount: Object.prototype.hasOwnProperty.call(raw, 'physicalCount') ? Math.max(0, Math.floor(toNumber(raw.physicalCount, 0))) : undefined,
    virtualCount: Object.prototype.hasOwnProperty.call(raw, 'virtualCount') ? Math.max(0, Math.floor(toNumber(raw.virtualCount, 0))) : undefined,
    availabilityKind: ['standard', 'physical_only', 'virtual_only', 'mixed', 'none'].includes(toStringValue(raw.availabilityKind))
      ? toStringValue(raw.availabilityKind) as NumberAvailabilityKind
      : undefined,
    guaranteedNumberTypes: Array.isArray(raw.guaranteedNumberTypes)
      ? raw.guaranteedNumberTypes.filter((value): value is NumberType => value === 'standard' || value === 'physical' || value === 'virtual')
      : undefined,
    currency: hasPrice ? 'USD' : undefined,
  };
}

function normalizeAvailabilitySummary(raw: unknown): CatalogAvailabilitySummary {
  const source = isRecord(raw) ? raw : {};
  return {
    physicalOnlyServices: Math.max(0, Math.floor(toNumber(source.physicalOnlyServices, 0))),
    virtualOnlyServices: Math.max(0, Math.floor(toNumber(source.virtualOnlyServices, 0))),
    mixedServices: Math.max(0, Math.floor(toNumber(source.mixedServices, 0))),
    unavailableServices: Math.max(0, Math.floor(toNumber(source.unavailableServices, 0))),
  };
}

function normalizeTopCountry(raw: unknown): TopCountryEntry | null {
  if (!isRecord(raw)) return null;
  const countryId = toNumber(raw.countryId, NaN);
  if (!Number.isFinite(countryId)) return null;

  const count = Math.max(0, Math.floor(toNumber(raw.count, 0)));
  const physicalCount = Object.prototype.hasOwnProperty.call(raw, 'physicalCount')
    ? Math.min(count, Math.max(0, Math.floor(toNumber(raw.physicalCount, 0))))
    : undefined;
  const virtualCount = Object.prototype.hasOwnProperty.call(raw, 'virtualCount')
    ? Math.min(count, Math.max(0, Math.floor(toNumber(raw.virtualCount, 0))))
    : undefined;

  return {
    countryId,
    count,
    physicalCount,
    virtualCount,
  };
}

function normalizeOperation(raw: unknown): OperationItem | null {
  if (!isRecord(raw)) return null;

  const id = toStringValue(raw.id);
  if (!id || !isUsdCurrency(raw.currency)) return null;

  const direction = raw.direction === 'credit' ? 'credit' : 'debit';
  const amount = Math.abs(toNumber(raw.amount, 0));
  const statusRaw = toStringValue(raw.status, 'pending');
  const date = toStringValue(raw.date || raw.createdAt, new Date().toISOString());

  return {
    id,
    type: toStringValue(raw.type, 'operation'),
    description: toStringValue(raw.description, 'Операция'),
    amount,
    direction,
    status: statusRaw,
    date,
    currency: 'USD',
  };
}

function normalizeDashboard(raw: unknown): DashboardData {
  const source = isRecord(raw) ? raw : {};
  const summary = isRecord(source.summary) ? source.summary : {};
  const timezone = toStringValue(summary.timezone);
  if (timezone !== 'UTC') {
    throw new ApiError('Dashboard не подтвердил календарную timezone UTC', 502, raw);
  }

  const latestOperations = Array.isArray(source.latestOperations) ? source.latestOperations : [];
  return {
    summary: {
      balance: toNumber(summary.balance, 0),
      currency: requireUsdCurrency(summary.currency, 'Dashboard'),
      timezone: 'UTC',
      activeNumbers: Math.max(0, Math.floor(toNumber(summary.activeNumbers, 0))),
      waitingSms: Math.max(0, Math.floor(toNumber(summary.waitingSms, 0))),
      spentToday: Math.max(0, toNumber(summary.spentToday, 0)),
      operationsToday: Math.max(0, Math.floor(toNumber(summary.operationsToday, 0))),
    },
    latestOperations: latestOperations
      .map((item) => normalizeOperation(item))
      .filter((item): item is OperationItem => item !== null),
  };
}

const NUMBER_STATUSES: NumberStatus[] = [
  'allocated',
  'waiting_sms',
  'waiting_retry',
  'waiting_resend',
  'received',
  'finished',
  'cancelled',
  'expired',
  'not_found',
];

function normalizeNumberStatus(raw: unknown): NumberStatus {
  const status = toStringValue(raw, 'allocated');
  return NUMBER_STATUSES.includes(status as NumberStatus) ? status as NumberStatus : 'allocated';
}

function normalizeSmsCodes(rawCodes: unknown, smsCode: string | null): string[] {
  const codes = Array.isArray(rawCodes)
    ? rawCodes
      .map((item) => toStringValue(item))
      .filter(Boolean)
    : [];

  if (smsCode && !codes.includes(smsCode)) {
    codes.push(smsCode);
  }

  return codes;
}

function normalizeSmsMessages(rawMessages: unknown) {
  if (!Array.isArray(rawMessages)) return [];
  return rawMessages
    .map((raw) => {
      if (!isRecord(raw)) return null;
      const id = toStringValue(raw.id);
      const text = toStringValue(raw.text);
      const receivedAt = toStringValue(raw.receivedAt);
      if (!id || !text || !receivedAt) return null;
      return {
        id,
        text,
        code: toStringValue(raw.code) || null,
        receivedAt,
      };
    })
    .filter((message): message is NonNullable<typeof message> => message !== null);
}

export function normalizeActiveNumber(raw: unknown): ActiveNumberItem | null {
  if (!isRecord(raw)) return null;

  const id = toStringValue(raw.id || raw.heroActivationId);
  if (!id || !isUsdCurrency(raw.currency)) return null;

  const serviceCode = toStringValue(raw.serviceCode || raw.service);
  const countryCode = toStringValue(raw.countryCode || raw.country);
  const heroActivationId = toStringValue(raw.heroActivationId || raw.activationId, id);

  const smsCode = toStringValue(raw.smsCode) || null;
  const retailPrice = toNumber(raw.retailUnitPrice ?? raw.retailPrice, 0);
  const markupPercent = toNumber(raw.markupPercent, 0);
  const explicitFinalPrice = toNumber(raw.finalUnitPrice ?? raw.finalPrice, NaN);
  const finalPrice = Number.isFinite(explicitFinalPrice)
    ? explicitFinalPrice
    : retailPrice + (retailPrice * (markupPercent / 100));

  return {
    id,
    heroActivationId,
    phoneNumber: toStringValue(raw.phoneNumber || raw.phone, '—'),
    serviceCode: serviceCode || 'unknown',
    serviceName: toStringValue(raw.serviceName || raw.serviceTitle) || undefined,
    countryCode: countryCode || '—',
    countryName: toStringValue(raw.countryName || raw.countryTitle) || undefined,
    operator: toStringValue(raw.operator) || null,
    status: normalizeNumberStatus(raw.status),
    smsCode,
    receivedSmsCodes: normalizeSmsCodes(raw.receivedSmsCodes, smsCode),
    receivedSmsMessages: normalizeSmsMessages(raw.receivedSmsMessages),
    purchasedAt: toStringValue(raw.purchasedAt || raw.createdAt),
    expiresAt: toStringValue(raw.expiresAt, ''),
    lastPolledAt: toStringValue(raw.lastPolledAt) || null,
    retailPrice,
    markupPercent,
    finalPrice,
    currency: 'USD',
    cancellationState: toStringValue(raw.cancellationState, 'none'),
    providerRefundState: toStringValue(raw.providerRefundState, 'not_evaluated'),
    customerRefundState: toStringValue(raw.customerRefundState, 'not_due'),
    cancelEligibleAt: toStringValue(raw.cancelEligibleAt) || null,
    refundOutcomeCode: toStringValue(raw.refundOutcomeCode, 'none'),
    refundOutcomeLabel: toStringValue(raw.refundOutcomeLabel) || null,
    canCancel: raw.canCancel === undefined ? undefined : raw.canCancel === true,
    supportsLifecycleActions: raw.supportsLifecycleActions === undefined
      ? true
      : raw.supportsLifecycleActions === true,
  };
}

function unwrapActivation(raw: unknown): unknown {
  if (isRecord(raw) && Object.prototype.hasOwnProperty.call(raw, 'activation')) {
    return raw.activation;
  }
  return raw;
}

async function runNumberAction(path: string): Promise<ActiveNumberItem> {
  const data = await requestJson<unknown>(path, { method: 'POST' });
  const activation = normalizeActiveNumber(unwrapActivation(data));
  if (!activation) {
    throw new ApiError('Backend вернул некорректную активацию', 502, data);
  }
  const [enriched] = await enrichActiveNumbers([activation]);
  return enriched || activation;
}

const countryNameCache = new Map<string, string>();
const serviceNameCache = new Map<string, string>();

async function ensureCountryNames(): Promise<void> {
  if (countryNameCache.size > 0) return;

  try {
    const countries = await fetchCountries({ visibleOnly: false });
    for (const country of countries) {
      countryNameCache.set(String(country.id), country.titleRu || country.titleEn || String(country.id));
    }
  } catch {
    // Названия не критичны для действий с активацией: оставим числовой код страны.
  }
}

async function ensureServiceName(item: ActiveNumberItem): Promise<void> {
  if (item.serviceName || serviceNameCache.has(item.serviceCode)) return;

  const countryId = Number(item.countryCode);
  if (!Number.isFinite(countryId)) return;

  try {
    const result = await fetchCatalogServices({
      countryId,
      serviceCode: item.serviceCode,
      limit: 1,
      offset: 0,
    });
    const service = result.services.find((entry) => entry.code === item.serviceCode) || result.services[0];
    if (service?.name) {
      serviceNameCache.set(item.serviceCode, service.name);
    }
    if (service?.country) {
      countryNameCache.set(String(service.country.id), service.country.titleRu || service.country.titleEn);
    }
  } catch {
    // Если каталог временно недоступен, показываем технический код сервиса.
  }
}

async function enrichActiveNumbers(items: ActiveNumberItem[]): Promise<ActiveNumberItem[]> {
  if (items.length === 0) return items;

  if (items.some((item) => !item.countryName)) {
    await ensureCountryNames();
  }

  const serviceLookups = Array.from(
    new Map(items.map((item) => [`${item.countryCode}:${item.serviceCode}`, item])).values(),
  );
  await Promise.all(serviceLookups.map((item) => ensureServiceName(item)));

  return items.map((item) => ({
    ...item,
    serviceName: item.serviceName || serviceNameCache.get(item.serviceCode) || item.serviceCode,
    countryName: item.countryName || countryNameCache.get(item.countryCode) || item.countryCode,
  }));
}

export async function fetchCurrentUser(): Promise<UserProfile> {
  const data = await requestJson<JsonRecord>('/api/me');

  const id = toNumber(data.id, 0);
  const email = toStringValue(data.email, `user-${id}@unknown.local`);
  const name = toStringValue(data.name, email.split('@')[0] || `user-${id}`);

  return {
    id,
    name,
    email,
    role: mapRole(data.role),
    balance: toNumber(data.balance, 0),
    currency: requireUsdCurrency(data.currency, 'Профиль пользователя'),
  };
}

export async function isServiceVisibleForCurrentUser(serviceSlug: string): Promise<boolean> {
  const data = await requestJson<JsonRecord>('/api/portal/sidebar-config');
  const services = Array.isArray(data.services) ? data.services : [];
  return services.some((item) => isRecord(item) && toStringValue(item.slug) === serviceSlug);
}

export async function fetchCountries(
  opts: { visibleOnly?: boolean; rentOnly?: boolean } = {},
): Promise<Country[]> {
  const params = new URLSearchParams();
  if (opts.visibleOnly !== undefined) params.set('visibleOnly', String(opts.visibleOnly));
  if (opts.rentOnly !== undefined) params.set('rentOnly', String(opts.rentOnly));

  const query = params.toString();
  const data = await requestJson<JsonRecord>(`/api/catalog/countries${query ? `?${query}` : ''}`);
  const rawCountries = Array.isArray(data.countries) ? data.countries : [];

  return rawCountries
    .map((item) => normalizeHeroCountry(item))
    .filter((item): item is Country => item !== null);
}

export async function fetchOperators(countryId: number, serviceCode?: string): Promise<string[]> {
  const params = new URLSearchParams({ country: String(countryId) });
  if (serviceCode) params.set('service', serviceCode);
  const data = await requestJson<JsonRecord>(`/api/catalog/operators?${params.toString()}`);
  if (serviceCode && !Array.isArray(data.options)) throw new Error('Не удалось подтвердить наличие у операторов. Повторите загрузку.');
  const rawOperators = serviceCode
    ? (data.options as JsonRecord[]).filter((item) => isRecord(item) && Number.isSafeInteger(item.count) && Number(item.count) > 0).map((item) => item.code)
    : Array.isArray(data.operators) ? data.operators : [];

  return Array.from(new Set(rawOperators
    .map((value) => toStringValue(value).toLowerCase())
    .filter((value) => value && value !== 'any' && /^[a-z0-9_-]{1,64}$/.test(value))))
    .sort((left, right) => left.localeCompare(right, 'en'));
}

export async function fetchRentCatalog(
  countryId: number,
  serviceCode?: string,
): Promise<RentCatalogService[]> {
  const params = new URLSearchParams({ country: String(countryId) });
  if (serviceCode) params.set('service', serviceCode);

  const data = await requestJson<JsonRecord>(`/api/catalog/rent-options?${params.toString()}`);
  const rawServices = Array.isArray(data.services) ? data.services : [];

  return rawServices.flatMap((rawService) => {
    if (!isRecord(rawService)) return [];
    const code = toStringValue(rawService.code);
    if (!code) return [];

    const options = (Array.isArray(rawService.options) ? rawService.options : [])
      .flatMap((rawOption) => {
        if (!isRecord(rawOption)) return [];
        const hours = toNumber(rawOption.hours, 0);
        const quantity = toNumber(rawOption.quantity, 0);
        const providerPrice = toNumber(rawOption.providerPrice, 0);
        if (hours <= 0 || quantity <= 0 || providerPrice <= 0 || rawOption.currency !== 'USD') {
          return [];
        }
        return [{ hours, quantity, providerPrice, currency: 'USD' as const }];
      })
      .sort((left, right) => left.hours - right.hours);

    if (options.length === 0) return [];
    return [{
      code,
      name: toStringValue(rawService.name, code),
      options,
    }];
  });
}

export async function fetchRentAvailability(): Promise<RentAvailability> {
  const data = await requestJson<JsonRecord>('/api/purchase/rent-availability');

  if (
    typeof data.rentAvailable !== 'boolean'
    || typeof data.reasonCode !== 'string'
    || typeof data.reason !== 'string'
    || typeof data.checkedAt !== 'string'
  ) {
    throw new ApiError('Сервер вернул неверное состояние аренды', 502);
  }

  return {
    rentAvailable: data.rentAvailable,
    reasonCode: data.reasonCode,
    reason: data.reason,
    checkedAt: data.checkedAt,
    durationUnit: data.durationUnit === 'hours' ? 'hours' : null,
    supportedDurations: Array.isArray(data.supportedDurations)
      ? data.supportedDurations.map((value) => toNumber(value, 0)).filter((value) => value > 0)
      : [],
    exactPriceConfirmed: data.exactPriceConfirmed === true,
    lifecycleConfirmed: data.lifecycleConfirmed === true,
  };
}

export async function fetchCatalogServices(opts: {
  all?: boolean;
  countryId?: number;
  serviceCode?: string;
  q?: string;
  limit?: number;
  offset?: number;
  topOnly?: boolean;
  numberType?: NumberType;
} = {}): Promise<{
  services: CatalogServiceItem[];
  popularServices: CatalogServiceItem[];
  total: number;
  adminMarkup: CatalogMarkup;
  availabilitySummary: CatalogAvailabilitySummary;
}> {
  const params = new URLSearchParams();
  if (opts.all) params.set('all', 'true');
  if (opts.countryId !== undefined) params.set('country', String(opts.countryId));
  if (opts.serviceCode) params.set('service', opts.serviceCode);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts.offset !== undefined) params.set('offset', String(opts.offset));
  if (opts.topOnly !== undefined) params.set('topOnly', String(opts.topOnly));
  if (opts.numberType) params.set('numberType', opts.numberType);

  const query = params.toString();
  const data = await requestJson<JsonRecord>(`/api/catalog/services${query ? `?${query}` : ''}`);
  const rawServices = Array.isArray(data.services) ? data.services : [];
  const meta = isRecord(data.meta) ? data.meta : {};
  const rawPopularServices = Array.isArray(meta.popularServices) ? meta.popularServices : [];

  return {
    services: rawServices
      .map((item) => normalizeCatalogService(item))
      .filter((item): item is CatalogServiceItem => item !== null),
    popularServices: rawPopularServices
      .map((item) => normalizeCatalogService(item))
      .filter((item): item is CatalogServiceItem => item !== null),
    total: toNumber(data.total, 0),
    adminMarkup: normalizeCatalogMarkup(data.adminMarkup),
    availabilitySummary: normalizeAvailabilitySummary(meta.availabilitySummary),
  };
}

export async function fetchTopCountriesByService(serviceCode: string): Promise<TopCountryEntry[]> {
  const params = new URLSearchParams({ service: serviceCode });
  const data = await requestJson<JsonRecord>(`/api/catalog/top-countries?${params.toString()}`);
  const rawEntries = Array.isArray(data.entries) ? data.entries : [];

  return rawEntries
    .map((item) => normalizeTopCountry(item))
    .filter((item): item is TopCountryEntry => item !== null);
}

export interface CatalogInventory {
  totals: Record<string, number>;
  countriesByService: Record<string, Record<number, number>>;
  checkedAt: string;
}

export async function fetchCatalogInventory(): Promise<CatalogInventory> {
  const data = await requestJson<CatalogInventory>('/api/catalog/inventory');
  if (!isRecord(data.totals) || !isRecord(data.countriesByService) || typeof data.checkedAt !== 'string') {
    throw new Error('Не удалось загрузить наличие номеров. Повторите загрузку.');
  }
  return data;
}

export async function fetchSystemInfo(): Promise<SystemInfo> {
  const data = await requestJson<JsonRecord>('/api/system/info');
  const effectiveModeRaw = toStringValue(data.effectiveMode, 'mock').toLowerCase();

  return {
    requestedMode: toStringValue(data.requestedMode, 'mock'),
    effectiveMode: effectiveModeRaw === 'live' ? 'live' : 'mock',
    allowLivePurchase: Boolean(data.allowLivePurchase),
    authServiceUrl: toStringValue(data.authServiceUrl),
    port: toNumber(data.port, 5004),
    nodeEnv: toStringValue(data.nodeEnv, 'development'),
  };
}

export async function fetchOperations(): Promise<OperationItem[]> {
  const data = await requestJson<unknown[]>('/api/operations');
  const rawList = Array.isArray(data) ? data : [];
  return rawList
    .map((item) => normalizeOperation(item))
    .filter((item): item is OperationItem => item !== null);
}

export async function fetchDashboard(): Promise<DashboardData> {
  const data = await requestJson<unknown>('/api/dashboard');
  return normalizeDashboard(data);
}

export async function fetchActiveNumbers(): Promise<ActiveNumberItem[]> {
  const data = await requestJson<unknown[]>('/api/numbers/active');
  const rawList = Array.isArray(data) ? data : [];

  const items = rawList
    .map((item) => normalizeActiveNumber(item))
    .filter((item): item is ActiveNumberItem => item !== null);

  return enrichActiveNumbers(items);
}

export async function pollNumberNow(id: string): Promise<ActiveNumberItem> {
  return runNumberAction(`/api/numbers/${encodeURIComponent(id)}/poll-now`);
}

export async function requestAnotherSms(id: string): Promise<ActiveNumberItem> {
  return runNumberAction(`/api/numbers/${encodeURIComponent(id)}/request-again`);
}

export async function finishActivation(id: string): Promise<ActiveNumberItem> {
  return runNumberAction(`/api/numbers/${encodeURIComponent(id)}/finish`);
}

export async function cancelActivation(id: string): Promise<ActiveNumberItem> {
  return runNumberAction(`/api/numbers/${encodeURIComponent(id)}/cancel`);
}

/** @deprecated Партнёрка выпилена из UI; функция сохранена под будущую задачу. */
export async function fetchPartnerSummary(): Promise<PartnerSnapshot> {
  const data = await requestJson<JsonRecord>('/api/partner/summary');
  const currency = requireUsdCurrency(data.currency, 'Партнёрская статистика');

  const topReferrals = Array.isArray(data.topReferrals) ? data.topReferrals : [];
  return {
    currency,
    referralCode: toStringValue(data.referralCode, '—'),
    percentRate: toNumber(data.percentRate, 0),
    invitedUsers: toNumber(data.invitedUsers, 0),
    activeUsers: toNumber(data.activeUsers, 0),
    paidThisMonth: toNumber(data.paidThisMonth, 0),
    pendingPayout: toNumber(data.pendingPayout, 0),
    topReferrals: topReferrals
      .filter((item): item is JsonRecord => isRecord(item))
      .filter((item) => isUsdCurrency(item.currency))
      .map((item, index) => ({
        id: toStringValue(item.id, `ref-${index + 1}`),
        email: toStringValue(item.email, 'unknown@example.com'),
        spent: toNumber(item.spent, 0),
        reward: toNumber(item.reward, 0),
        currency: 'USD',
      })),
  };
}

export async function getPurchaseQuote(payload: PurchaseQuotePayload): Promise<PurchaseQuoteResult> {
  const result = await requestJson<PurchaseQuoteResult>('/api/purchase/quote', {
    method: 'POST',
    body: JSON.stringify({
      serviceCode: payload.serviceCode,
      countryCode: payload.countryId,
      operator: payload.operator,
      quantity: payload.quantity,
      numberType: payload.numberType,
      priceAfter: payload.priceAfter,
    }),
  });

  requireUsdCurrency(result.currency, 'Расчёт покупки');
  return result;
}

export async function createPurchase(
  payload: PurchaseQuotePayload,
  operationKey: string,
  confirmedQuote: Pick<PurchaseQuoteResult, 'finalUnitPrice' | 'finalTotal'>,
): Promise<PurchaseBuyResult> {
  const result = await requestJson<PurchaseBuyResult>('/api/purchase/buy', {
    method: 'POST',
    body: JSON.stringify({
      serviceCode: payload.serviceCode,
      countryCode: payload.countryId,
      operator: payload.operator,
      quantity: payload.quantity,
      numberType: payload.numberType,
      priceAfter: payload.priceAfter,
      operationKey,
      expectedFinalUnitPrice: confirmedQuote.finalUnitPrice,
      expectedFinalTotal: confirmedQuote.finalTotal,
    }),
  });

  requirePurchaseCurrency(result);
  return result;
}

export async function fetchPurchaseOperation(operationKey: string): Promise<PurchaseBuyResult> {
  const result = await requestJson<PurchaseBuyResult>(
    `/api/purchase/operations/${encodeURIComponent(operationKey)}`,
  );
  requirePurchaseCurrency(result);
  return result;
}

function requirePurchaseCurrency(result: PurchaseBuyResult): void {
  requireUsdCurrency(result.currency, 'Результат покупки');
  requireUsdCurrency(result.totals?.currency, 'Итог покупки');
  requireUsdCurrency(result.order?.currency, 'Заказ');

  for (const item of result.successful || []) {
    requireUsdCurrency(item.currency, 'Списание за номер');
  }
}

export interface LongRentalCatalogSelection {
  rentalScope: LongRentalScope;
  rentalCapability?: 'sms' | 'sms_voice';
  serviceCode?: string | null;
  stateName?: string | null;
  areaCode?: string | null;
}

function longRentalSelectionParams(selection: LongRentalCatalogSelection): URLSearchParams {
  const params = new URLSearchParams({ rentalScope: selection.rentalScope });
  if (selection.rentalCapability) params.set('rentalCapability', selection.rentalCapability);
  if (selection.serviceCode) params.set('serviceCode', selection.serviceCode);
  if (selection.stateName) params.set('stateName', selection.stateName);
  if (selection.areaCode) params.set('areaCode', selection.areaCode);
  return params;
}

export async function fetchLongRentalCatalog(
  selection: LongRentalCatalogSelection = { rentalScope: 'all_services' },
): Promise<LongRentalCatalog> {
  const data = await requestJson<LongRentalCatalog>(`/api/long-rentals/catalog?${longRentalSelectionParams(selection).toString()}`);
  if (data.geo?.code !== 'US' || !Array.isArray(data.offers)) {
    throw new ApiError('Сервер вернул некорректный каталог долгосрочной аренды', 502, data);
  }
  return data;
}

export async function createLongRentalQuote(
  durationDays: number,
  selection: LongRentalCatalogSelection,
): Promise<LongRentalQuote> {
  const data = await requestJson<LongRentalQuote>('/api/long-rentals/quotes', {
    method: 'POST',
    body: JSON.stringify({ geoCode: 'US', durationDays, ...selection }),
  });
  requireUsdCurrency(data.currency, 'Предложение долгосрочной аренды');
  return data;
}

export async function purchaseLongRental(
  quoteId: string,
  operationKey: string,
): Promise<LongRentalPurchaseOrder> {
  const data = await requestJson<LongRentalPurchaseOrder>('/api/long-rentals/purchase', {
    method: 'POST',
    body: JSON.stringify({ quoteId, operationKey, termsAccepted: true }),
  });
  return data;
}

export async function fetchLongRentalOrder(orderId: string): Promise<LongRentalPurchaseOrder> {
  return requestJson<LongRentalPurchaseOrder>(`/api/long-rentals/orders/${encodeURIComponent(orderId)}`);
}

export async function reconcileLongRentalOrder(orderId: string): Promise<LongRentalPurchaseOrder> {
  return requestJson<LongRentalPurchaseOrder>(`/api/long-rentals/orders/${encodeURIComponent(orderId)}/reconcile`, { method: 'POST' });
}

export async function fetchLongRentalNumbers(): Promise<LongRentalNumber[]> {
  const data = await requestJson<LongRentalNumber[]>('/api/long-rentals/numbers');
  return Array.isArray(data) ? data : [];
}

export async function fetchPublicLongRentalInbox(
  shareToken: string,
  phoneNumber: string,
): Promise<PublicLongRentalInbox> {
  return requestPublicLongRental<PublicLongRentalInbox>('/inbox', { shareToken, phoneNumber });
}

async function requestPublicLongRental<T>(path: string, body: Record<string, unknown>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}/api/public/long-rentals${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError('Сервер SMS недоступен', 503);
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new ApiError(getErrorMessage(payload, response.status), response.status, payload);
  }
  return payload?.data as T;
}

export function wakePublicLongRental(shareToken: string, phoneNumber: string): Promise<PublicLongRentalInbox> {
  return requestPublicLongRental('/wake', { shareToken, phoneNumber });
}

export function previewPublicLongRentalRenewal(shareToken: string, phoneNumber: string): Promise<LongRentalRenewalPreview> {
  return requestPublicLongRental('/renewal-preview', { shareToken, phoneNumber });
}

export function fetchPublicLongRentalPaymentOptions(shareToken: string, phoneNumber: string): Promise<Array<{ asset: string; network: string }>> {
  return requestPublicLongRental('/payment-options', { shareToken, phoneNumber });
}

export function createPublicLongRentalInvoice(
  shareToken: string, phoneNumber: string, asset: string, network: string, expectedFinalAmount: number,
): Promise<PublicLongRentalInvoice> {
  return requestPublicLongRental('/invoices', { shareToken, phoneNumber, asset, network, expectedFinalAmount, termsAccepted: true });
}

export function fetchPublicLongRentalInvoiceStatus(shareToken: string, phoneNumber: string, id: string): Promise<PublicLongRentalInvoice> {
  return requestPublicLongRental(`/invoices/${encodeURIComponent(id)}/status`, { shareToken, phoneNumber });
}

export function addSharedLongRentalNumber(shareToken: string, phoneNumber: string): Promise<{ added: boolean; alreadyOwned: boolean }> {
  return requestJson('/api/long-rentals/shared-numbers', {
    method: 'POST', body: JSON.stringify({ shareToken, phoneNumber }),
  }, false);
}

export function fetchSharedLongRentalNumbers(): Promise<SharedLongRentalNumber[]> {
  return requestJson('/api/long-rentals/shared-numbers');
}

export function removeSharedLongRentalNumber(id: string): Promise<void> {
  return requestJson(`/api/long-rentals/shared-numbers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function rotateLongRentalShare(id: string): Promise<LongRentalNumber> {
  return requestJson(`/api/long-rentals/numbers/${encodeURIComponent(id)}/rotate-share`, { method: 'POST' });
}

export function disableLongRentalShare(id: string): Promise<LongRentalNumber> {
  return requestJson(`/api/long-rentals/numbers/${encodeURIComponent(id)}/disable-share`, { method: 'POST' });
}

export async function syncLongRentalNumber(id: string): Promise<LongRentalNumber> {
  return requestJson<LongRentalNumber>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/sync`, { method: 'POST' });
}

export async function wakeLongRentalNumber(id: string): Promise<LongRentalNumber> {
  return requestJson<LongRentalNumber>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/wake`, { method: 'POST' });
}

export async function createLongRentalCallAccess(id: string): Promise<{ token: string; rentalId: string; expiresAt: string | null }> {
  return requestJson<{ token: string; rentalId: string; expiresAt: string | null }>(
    `/api/long-rentals/numbers/${encodeURIComponent(id)}/call-access`,
    { method: 'POST' },
  );
}

export async function disableLongRentalRenewal(id: string): Promise<LongRentalNumber> {
  return requestJson<LongRentalNumber>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/cancel-renewal`, {
    method: 'POST',
    body: JSON.stringify({ operationKey: createClientOperationKey('long-cancel') }),
  });
}

export async function previewLongRentalRenewal(id: string): Promise<LongRentalRenewalPreview> {
  const data = await requestJson<LongRentalRenewalPreview>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/renewal-preview`, { method: 'POST' });
  requireUsdCurrency(data.currency, 'Предложение продления');
  return data;
}

export async function renewLongRental(
  id: string,
  preview: LongRentalRenewalPreview,
  operationKey: string,
): Promise<LongRentalNumber> {
  const data = await requestJson<{ rental: LongRentalNumber }>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/renew`, {
    method: 'POST',
    body: JSON.stringify({
      operationKey,
      expectedFinalAmount: preview.finalAmount,
      termsAccepted: true,
    }),
  });
  return data.rental;
}

export async function refundLongRental(id: string): Promise<LongRentalNumber> {
  return requestJson<LongRentalNumber>(`/api/long-rentals/numbers/${encodeURIComponent(id)}/refund`, {
    method: 'POST',
    body: JSON.stringify({ operationKey: `long-refund:${id}` }),
  });
}

export interface VoiceVerificationSelection {
  serviceCode?: string | null;
  stateName?: string | null;
  areaCode?: string | null;
}

function voiceSelectionParams(selection: VoiceVerificationSelection): URLSearchParams {
  const params = new URLSearchParams();
  if (selection.serviceCode) params.set('serviceCode', selection.serviceCode);
  if (selection.stateName) params.set('stateName', selection.stateName);
  if (selection.areaCode) params.set('areaCode', selection.areaCode);
  return params;
}

export async function fetchVoiceVerificationCatalog(
  selection: VoiceVerificationSelection = {},
): Promise<VoiceVerificationCatalog> {
  const params = voiceSelectionParams(selection);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const data = await requestJson<VoiceVerificationCatalog>(`/api/voice-verifications/catalog${suffix}`);
  if (data.geo?.code !== 'US' || !Array.isArray(data.services) || !Array.isArray(data.locations)) {
    throw new ApiError('Сервер вернул некорректный каталог Voice verification', 502, data);
  }
  if (data.offer?.currency) requireUsdCurrency(data.offer.currency, 'Предложение Voice verification');
  return data;
}

export async function createVoiceVerificationQuote(
  selection: VoiceVerificationSelection,
): Promise<VoiceVerificationQuote> {
  const data = await requestJson<VoiceVerificationQuote>('/api/voice-verifications/quotes', {
    method: 'POST',
    body: JSON.stringify({ geoCode: 'US', ...selection }),
  });
  requireUsdCurrency(data.currency, 'Предложение Voice verification');
  return data;
}

export async function purchaseVoiceVerification(
  quoteId: string,
  operationKey: string,
): Promise<VoiceVerificationPurchaseOrder> {
  return requestJson<VoiceVerificationPurchaseOrder>('/api/voice-verifications/purchase', {
    method: 'POST',
    body: JSON.stringify({ quoteId, operationKey, termsAccepted: true }),
  });
}

export async function fetchVoiceVerificationOrder(orderId: string): Promise<VoiceVerificationPurchaseOrder> {
  return requestJson<VoiceVerificationPurchaseOrder>(`/api/voice-verifications/orders/${encodeURIComponent(orderId)}`);
}

export async function reconcileVoiceVerificationOrder(orderId: string): Promise<VoiceVerificationPurchaseOrder> {
  return requestJson<VoiceVerificationPurchaseOrder>(`/api/voice-verifications/orders/${encodeURIComponent(orderId)}/reconcile`, { method: 'POST' });
}

export async function fetchVoiceVerifications(): Promise<VoiceVerificationNumber[]> {
  const data = await requestJson<VoiceVerificationNumber[]>('/api/voice-verifications');
  return Array.isArray(data) ? data : [];
}

export async function syncVoiceVerification(id: string): Promise<VoiceVerificationNumber> {
  return requestJson<VoiceVerificationNumber>(`/api/voice-verifications/${encodeURIComponent(id)}/sync`, { method: 'POST' });
}

export async function createVoiceCallAccess(id: string): Promise<{ token: string; verificationId: string; expiresAt: string }> {
  return requestJson<{ token: string; verificationId: string; expiresAt: string }>(
    `/api/voice-verifications/${encodeURIComponent(id)}/call-access`,
    { method: 'POST' },
  );
}

export async function cancelVoiceVerification(id: string, operationKey: string): Promise<VoiceVerificationNumber> {
  return requestJson<VoiceVerificationNumber>(`/api/voice-verifications/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ operationKey }),
  });
}
